import * as PIXI from 'pixi.js';
import { InputManager, StoryManager } from '@/systems';
import { ItemConfig, itemsConfig } from '@/config/ItemsConfig';
import {Game} from "@/core";

export class InventoryDisplay {
  private container: PIXI.Container;
  private inspectPopupContainer: PIXI.Container | null = null;
  private background: PIXI.Container;
  private backgroundSprite!: PIXI.Sprite;
  private headerText: PIXI.Text;
  private itemsContainer: PIXI.Container;
  private toggleButton: PIXI.Container;
  private toggleSprite!: PIXI.Sprite;
  private closeButton!: PIXI.Container;
  private storyManager: StoryManager;
  private isExpanded: boolean = false;
  private screenWidth: number;
  private screenHeight: number;

  // Display configuration
  private readonly collapsedWidth = 150
  private readonly collapsedHeight = 150;
  
  private expandedWidth = 600; 
  private expandedHeight = 500;
  
  // Grid Configuration
  private readonly cols = 3;
  private readonly gridSpacingX = 200; 
  private readonly gridSpacingY = 180; 

  private readonly gridOffsetY = 170; 

  private itemsPerPage = 9; // 3x3 grid
  
  // Pagination
  private currentPage = 0;
  private paginationContainer!: PIXI.Container;
  private prevButton!: PIXI.Container;
  private nextButton!: PIXI.Container;
  private pageText!: PIXI.Text;

  private darkOverlay!: PIXI.Graphics;

  constructor(screenWidth: number, screenHeight: number) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.storyManager = StoryManager.getInstance();
    this.container = new PIXI.Container();

    // Create dark overlay
    this.darkOverlay = new PIXI.Graphics();
    this.darkOverlay.visible = false;
    this.darkOverlay.interactive = true;
    this.darkOverlay.cursor = 'default';
    this.darkOverlay.on('pointerup', () => this.toggle());
    this.container.addChild(this.darkOverlay);

    this.background = new PIXI.Container();
    
    this.backgroundSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
    this.background.addChild(this.backgroundSprite);

    // Create header text
    this.headerText = new PIXI.Text('Inventory', {
      fontFamily: '"Erben Gothic", serif',
      fontSize: 32, 
      fill: 0x4a3c31,
      fontWeight: 'bold',
      padding: 4,
    });
    this.headerText.resolution = window.devicePixelRatio || 2;
    this.headerText.anchor.set(0.5);
    this.headerText.y = 40;

    // Create Close Button
    this.closeButton = new PIXI.Container();
    const closeIcon = new PIXI.Text('X', {
        fontFamily: '"Erben Gothic", serif',
        fontSize: 24,
        fill: 0x4a3c31,
        fontWeight: 'bold',
        padding: 4,
    });
    closeIcon.resolution = window.devicePixelRatio || 2;
    closeIcon.anchor.set(0.5);
    
    // Hit area for easier clicking
    const hitArea = new PIXI.Graphics();
    hitArea.beginFill(0x000000, 0.01);
    hitArea.drawCircle(0, 0, 20);
    hitArea.endFill();
    
    this.closeButton.addChild(hitArea);
    this.closeButton.addChild(closeIcon);
    this.closeButton.interactive = true;
    this.closeButton.cursor = 'pointer';
    this.closeButton.on('pointerup', () => this.toggle());

    // Create items container
    this.itemsContainer = new PIXI.Container();

    // Create pagination controls
    this.paginationContainer = new PIXI.Container();
    this.createPaginationControls();

    // Create toggle button (collapsed state)
    this.toggleButton = new PIXI.Container();

    // Create sprite placeholder or init
    this.toggleSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
    this.toggleSprite.width = this.collapsedWidth;
    this.toggleSprite.height = this.collapsedHeight;
    this.toggleSprite.anchor.set(0); 
    
    this.toggleButton.addChild(this.toggleSprite);
    this.toggleButton.interactive = true;
    this.toggleButton.cursor = 'pointer';
    this.toggleButton.on('pointerup', () => this.toggle());

    // Load textures
    this.loadTextures();

    // Add all elements to container
    this.container.addChild(this.background);
    this.background.addChild(this.headerText);
    this.background.addChild(this.itemsContainer);
    this.background.addChild(this.closeButton);
    this.background.addChild(this.paginationContainer);
    
    this.container.addChild(this.toggleButton);

    this.background.interactive = true;

    // Position in bottom-right corner
    this.updatePosition(screenWidth, screenHeight);

    // Start in collapsed state
    this.setCollapsed();

    // Listen for inventory changes
    this.storyManager.addInventoryEventListener('inventoryChanged', () => {
      // Reset to first page when inventory changes
      this.currentPage = 0;
      this.updateDisplay();
    });

    // Listen for inventory changes
    this.storyManager.addInventoryEventListener('showInventory', (itemConfig?:ItemConfig) => {
      // Reset to first page when inventory changes
      if(itemConfig){
        this.showInspectPopup(itemConfig)
      }
    });

    // Initial update
    this.updateDisplay();
  }

  public getExpanded=()=>{
    return this.isExpanded
  }

  private async loadTextures(): Promise<void> {
    try {
        const texture1 = await PIXI.Assets.load('/assets/ui/b_inventory2.png');
        const texture2 = await PIXI.Assets.load('/assets/ui/b_inventory1.png');
        const bgTexture = await PIXI.Assets.load('/assets/ui/inventory_background.png');
        
        // Initial texture
        this.toggleSprite.texture = texture1;
        this.backgroundSprite.texture = bgTexture;
        
        if (bgTexture) {
            this.expandedWidth = bgTexture.width;
            this.expandedHeight = bgTexture.height;
            this.headerText.x = this.expandedWidth / 2;
            
            // Adjust close button position
            this.closeButton.x = this.expandedWidth - 30;
            this.closeButton.y = 30;
            
            // Re-center pagination
            this.updatePaginationPosition();
            
            // Re-render items to center them
            this.updateDisplay();
        }

        // Interaction logic using loaded textures
        this.toggleButton.on('pointerdown', () => {
            this.toggleSprite.texture = texture2;
        });
        this.toggleButton.on('pointerup', () => {
            this.toggleSprite.texture = texture1; 
        });
        this.toggleButton.on('pointerout', () => {
             this.toggleSprite.texture = texture1;
        });
        
    } catch (e) {
        console.error('Failed to load inventory assets:', e);
        // Fallback or debug color
        this.toggleSprite.tint = 0xFF0000; // Red tint to indicate error
    }
  }



  private createPaginationControls(): void {
    // Previous button
    this.prevButton = new PIXI.Container();
    const prevText = new PIXI.Text('<', {
      fontFamily: '"Erben Gothic", serif',
      fontSize: 30,
      fill: 0x4a3c31,
      fontWeight: 'bold',
      padding: 4,
    });
    prevText.resolution = window.devicePixelRatio || 2;
    prevText.anchor.set(0.5);
    this.prevButton.addChild(prevText);
    this.prevButton.interactive = true;
    this.prevButton.cursor = 'pointer';
    this.prevButton.on('pointerup', () => this.previousPage());
    
    // Page text
    this.pageText = new PIXI.Text('1/1', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 20,
      fill: 0x4a3c31,
      padding: 4,
    });
    this.pageText.resolution = window.devicePixelRatio || 2;
    this.pageText.anchor.set(0.5);
    
    // Next button
    this.nextButton = new PIXI.Container();
    const nextText = new PIXI.Text('>', {
      fontFamily: '"Erben Gothic", serif',
      fontSize: 30,
      fill: 0x4a3c31,
      fontWeight: 'bold',
      padding: 4,
    });
    nextText.resolution = window.devicePixelRatio || 2;
    nextText.anchor.set(0.5);
    this.nextButton.addChild(nextText);
    this.nextButton.interactive = true;
    this.nextButton.cursor = 'pointer';
    this.nextButton.on('pointerup', () => this.nextPage());
    
    // Add to pagination container
    this.paginationContainer.addChild(this.prevButton);
    this.paginationContainer.addChild(this.pageText);
    this.paginationContainer.addChild(this.nextButton);
    
    // Position at bottom of inventory
    this.updatePaginationPosition();
  }

  private updatePaginationPosition(): void {
    const spacing = 40;
    
    // Center the pagination controls
    const startX = this.expandedWidth / 2;
    const bottomY = this.expandedHeight - 50;

    this.prevButton.x = startX - spacing;
    this.prevButton.y = bottomY;
    
    this.pageText.x = startX;
    this.pageText.y = bottomY;
    
    this.nextButton.x = startX + spacing;
    this.nextButton.y = bottomY;
  }

  private previousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.updateDisplay();
    }
  }

  private nextPage(): void {
    const inventory = this.storyManager.getInventory();
    const totalPages = Math.ceil(inventory.size / this.itemsPerPage);
    if (this.currentPage < totalPages - 1) {
      this.currentPage++;
      this.updateDisplay();
    }
  }

  private updateDisplay(): void {
    // Clear existing items
    this.itemsContainer.removeChildren();

    if (!this.isExpanded) {
      // In collapsed state, just show the button (handled by sprite textures)
      return;
    }
    
    // Check if itemsConfig is defined (it should be imported)
    const allItems = itemsConfig; 
    const totalPages = Math.ceil(allItems.length / this.itemsPerPage);

    // Update pagination visibility
    if (totalPages > 1) {
      this.paginationContainer.visible = true;
      this.pageText.text = `${this.currentPage + 1}/${totalPages}`;
      this.prevButton.alpha = this.currentPage > 0 ? 1 : 0.3;
      this.nextButton.alpha = this.currentPage < totalPages - 1 ? 1 : 0.3;
    } else {
      this.paginationContainer.visible = false;
    }

    // Calculate which items to show on current page
    const startIndex = this.currentPage * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, allItems.length);
    const pageItems = allItems.slice(startIndex, endIndex);

    // Display items in grid
    pageItems.forEach((itemConfig, index) => {
        // Calculate grid position
        const col = index % this.cols;
        const row = Math.floor(index / this.cols);
        
        // Calculate center offset
        const totalGridWidth = (this.cols - 1) * this.gridSpacingX;
        const startX = (this.expandedWidth - totalGridWidth) / 2;
        
        const xPos = startX + (col * this.gridSpacingX);
        const yPos = this.gridOffsetY + (row * this.gridSpacingY);

        const itemContainer = new PIXI.Container();
        itemContainer.x = xPos;
        itemContainer.y = yPos;
        
        // Check Status
        // Owned: In current inventory
        // Discovered: In global discovered items (localstorage)
        // Unknown: Neither
        
        const isOwned = this.storyManager.hasItem(itemConfig.id);
        const isDiscovered = this.storyManager.isItemDiscovered(itemConfig.id);
        
        if (isOwned || isDiscovered) {
             // Show Image (Normal or Silhouette)
            let imageSprite: PIXI.Sprite;
            try {
                imageSprite = PIXI.Sprite.from(itemConfig.imageUrl);
            } catch {
                imageSprite = PIXI.Sprite.from(PIXI.Texture.WHITE);
                imageSprite.tint = 0x888888;
            }

            // Scale image
            const isMobile = InputManager.getInstance().isMobile();
            const maxSize = isMobile ? 160 : 180; 

            // Visual Style
            if (isOwned) {
                 imageSprite.tint = 0xFFFFFF; // Normal
            } else {
                 // Discovered but not owned -> Silhouette
                 imageSprite.tint = 0x000000; // Black
                 imageSprite.alpha = 0.7;
            }
            
            // Scale logic matches previous
             const scale = Math.min(maxSize / (imageSprite.width || 100), maxSize / (imageSprite.height || 100));
            imageSprite.scale.set(scale); // Re-calc this if needed
            
            // Fix scale since sprite might be 1x1 if loading
             if(imageSprite.texture.label !== 'EMPTY') {
                 // It might be loaded
                  const s = Math.min(maxSize / imageSprite.texture.width, maxSize / imageSprite.texture.height);
                  imageSprite.scale.set(s);
             }

            imageSprite.anchor.set(0.5);
        
            itemContainer.addChild(imageSprite);
            
            // Quantity badge
            if (isOwned) {
                const quantity = this.storyManager.getItemQuantity(itemConfig.id);
                 if (quantity > 1) {
                    const qtyBg = new PIXI.Graphics();
                    qtyBg.beginFill(0xffd700, 1);
                    qtyBg.drawCircle(30, 30, 15);
                    qtyBg.endFill();
                    itemContainer.addChild(qtyBg);

                    const qtyText = new PIXI.Text(quantity.toString(), {
                        fontFamily: 'Rosarivo, serif',
                        fontSize: 14,
                        fontWeight: 'bold',
                        fill: 0x000000,
                        padding: 4,
                    });
                    qtyText.resolution = window.devicePixelRatio || 2;
                    qtyText.anchor.set(0.5);
                    qtyText.x = 30;
                    qtyText.y = 30;
                    itemContainer.addChild(qtyText);
                }
            }

            // Interaction
            if (isOwned) {
                itemContainer.interactive = true;
                itemContainer.cursor = 'pointer';

                // Hover effects
                itemContainer.on('pointerover', () => {
                    imageSprite.scale.x *= 1.1;
                    imageSprite.scale.y *= 1.1;
                });
                itemContainer.on('pointerout', () => {
                     const isMobileOut = InputManager.getInstance().isMobile();
                     const maxSizeOut = isMobileOut ? 160 : 180;
                     const s = Math.min(maxSizeOut / imageSprite.texture.width, maxSizeOut / imageSprite.texture.height);
                     imageSprite.scale.set(s);
                });

                itemContainer.on('pointerup', () => {
                    this.showInspectPopup(itemConfig);
                });
            }

        } else {
            // UNKNOWN STATE (?)
             const unknownText = new PIXI.Text('?', {
                fontFamily: "Erben Gothic",
                fontSize: 64,
                fontWeight: 'normal', 
                fill: 0x4a3c31,
                padding: 4,
            });
            unknownText.resolution = window.devicePixelRatio || 2;
            unknownText.alpha = 0.5;
            unknownText.anchor.set(0.5);
            unknownText.x = 10; 
            itemContainer.addChild(unknownText);
        }

        this.itemsContainer.addChild(itemContainer);
    });
  }

  public toggle(): void {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) {
      this.setExpanded();
      this.hideUIElements()
    } else {
      this.setCollapsed();
      this.showUIElements()
    }
    this.updateDisplay();
  }

  private hideUIElements=()=>{
    //IMPORTANT! Other state is managed in Game.ts gameloop
    let gameInstance = Game.getInstance()
    gameInstance.getDayDisplay().setVisible(false)
  }

  private showUIElements=()=>{
    //IMPORTANT! Other state is managed in Game.ts gameloop
    let gameInstance = Game.getInstance()
    gameInstance.getDayDisplay().setVisible(true)
  }


  private setExpanded(): void {
    this.isExpanded = true;
    this.background.visible = true;
    this.toggleButton.visible = true; 
    
    this.updatePosition(this.screenWidth, this.screenHeight);
  }

  private setCollapsed(): void {
    this.isExpanded = false;
    this.background.visible = false;
    this.toggleButton.visible = true;

    this.updatePosition(this.screenWidth, this.screenHeight);
  }

  public updatePosition(screenWidth: number, screenHeight: number): void {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Apply dynamic scaling
    const referenceWidth = 1280;
    const isMobile = InputManager.getInstance().isMobile();
    const isTablet = InputManager.getInstance().isTablet();
    
    let scale = screenWidth / referenceWidth;
    
    if (isMobile && this.isExpanded) {
        const targetHeightRatio = isTablet ? 0.92 : 0.85; // 85% of screen height on mobile, 92% on tablet
        const heightScale = (screenHeight * targetHeightRatio) / this.expandedHeight;
        
        scale = Math.min(scale, heightScale);
        
        scale = Math.max(isTablet ? 0.6 : 0.45, Math.min(isTablet ? 0.95 : 0.85, scale));
    } else {
        const minScale = 0.7;
        const maxScale = 1.0;
        scale = Math.max(minScale, Math.min(maxScale, scale));
    }

    this.container.scale.set(scale);

    if (this.isExpanded) {
        this.container.x = 0;
        this.container.y = 0;

        // Show dark overlay
        this.darkOverlay.clear();
        this.darkOverlay.beginFill(0x000000, 0.85);
        this.darkOverlay.drawRect(0, 0, this.screenWidth / scale, this.screenHeight / scale);
        this.darkOverlay.endFill();
        this.darkOverlay.visible = true;

        const screenCenterX = this.screenWidth / 2 / scale;
        const screenCenterY = this.screenHeight / 2 / scale;
        
        this.background.x = screenCenterX - (this.expandedWidth / 2);
        this.background.y = screenCenterY - (this.expandedHeight / 2);
        
        // Update pagination pos just in case
        this.updatePaginationPosition();

        const isMobile = InputManager.getInstance().isMobile();
        const targetScreenSize = 140; 
        
        const interactCenterFromRight = 120;
        const rightMargin = isMobile ? (interactCenterFromRight - targetScreenSize / 2) : 50;
        const bottomPosition = 20 + 140 + 20; 

        const screenButtonX = this.screenWidth - targetScreenSize - rightMargin;
        const screenButtonY = this.screenHeight - targetScreenSize - bottomPosition;

        // Container is at (0,0) scaled, so just divide screen coords by scale
        this.toggleButton.x = screenButtonX;
        this.toggleButton.y = screenButtonY;

        this.toggleSprite.width = 0
        this.toggleSprite.height = 0;

    } else {
        // Hide overlay when collapsed
        this.darkOverlay.visible = false;

        const targetScreenSize = 140;
        this.toggleSprite.width = targetScreenSize;
        this.toggleSprite.height = targetScreenSize;

        const isMobileCollapsed = InputManager.getInstance().isMobile();
        const rightMargin = 20 * scale;
        const bottomMargin = 20 * scale;
        const gap = isMobileCollapsed ? 5 * scale : 20 * scale;
        const buttonSize = targetScreenSize * scale;

        // Position above interaction button (20px gap)
        const screenX = this.screenWidth - buttonSize - rightMargin;
        const screenY = this.screenHeight - (buttonSize * 2) - bottomMargin - gap;
        
        this.container.x = screenX;
        this.container.y = screenY;

        // Button is at (0,0) inside container
        this.toggleButton.x = 0;
        this.toggleButton.y = 0;
    }
    
    // Update inspect popup if open
    if (this.inspectPopupContainer) {
       // Re-center logic if needed
       this.closeInspectPopup(); // Easiest way to handle resize for now
    }
  }

  public showInspectPopup(item: ItemConfig): void {
    if (this.inspectPopupContainer) {
        this.closeInspectPopup();
    }

    this.inspectPopupContainer = new PIXI.Container();
    
    const bg = new PIXI.Graphics();
    bg.beginFill(0x000000, 0.85); 
    
    const topLeft = this.container.toLocal(new PIXI.Point(0,0));
    const bottomRight = this.container.toLocal(new PIXI.Point(this.screenWidth, this.screenHeight));
    
    bg.drawRect(topLeft.x, topLeft.y, bottomRight.x - topLeft.x, bottomRight.y - topLeft.y);
    bg.endFill();
    
    // Make background interactive to close
    bg.interactive = true;
    bg.cursor = 'pointer';
    bg.on('pointerup', () => this.closeInspectPopup());
    
    this.inspectPopupContainer.addChild(bg);
    
    // Content Container
    const content = new PIXI.Container();
    const centerX = topLeft.x + (bottomRight.x - topLeft.x)/2;
    const centerY = topLeft.y + (bottomRight.y - topLeft.y)/2;
    
    content.x = centerX;
    content.y = centerY;

    const panelW = 600;
    const panelH = 580;
    
    const panelBg = new PIXI.Sprite(this.backgroundSprite.texture);
    panelBg.anchor.set(0.5);
    panelBg.width = panelW;
    panelBg.height = panelH;
    
    content.addChild(panelBg);

    // Item Image
    let imageSprite: PIXI.Sprite;
    try {
        imageSprite = PIXI.Sprite.from(item.imageUrl);
    } catch {
       imageSprite = PIXI.Sprite.from(PIXI.Texture.WHITE);
       imageSprite.tint = 0x888888;
    }
    
    // Clean scaling: fit within a box
    const maxImgSize = 450; 
    const scale =  Math.min(maxImgSize / imageSprite.width, maxImgSize / imageSprite.height);
    imageSprite.scale.set(scale);
    imageSprite.anchor.set(0.5);
    imageSprite.y = -panelH/2 + 210; 
    
    content.addChild(imageSprite);
    
    // Item Name
    const nameText = new PIXI.Text(item.name, {
        fontFamily: '"Erben Gothic", serif', 
        fontSize: 32, 
        fontWeight: 'bold',
        fill: 0x4a3c31,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: panelW - 100,
        padding: 4,
    });
    nameText.resolution = window.devicePixelRatio || 2;
    nameText.anchor.set(0.5);
    nameText.y = -panelH/2 + 420; 
    content.addChild(nameText);
    
    // Item Description
    const desc = item.description || "No description available.";
    const descText = new PIXI.Text(desc, {
        fontFamily: 'Rosarivo, serif',
        fontSize: 24, 
        fill: 0x4a3c31,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: panelW - 120,
        padding: 4,
    });
    descText.resolution = window.devicePixelRatio || 2;
    descText.anchor.set(0.5, 0); 
    descText.y = -panelH/2 + 460;
    content.addChild(descText);
    
    // Close hint (Click anywhere to close)
    const hintText = new PIXI.Text("(Click anywhere to close)", {
        fontFamily: 'Rosarivo, serif',
        fontSize: 16,
        fill: 0x666666,
        fontStyle: 'italic',
        padding: 4,
    });
    hintText.resolution = window.devicePixelRatio || 2;
    hintText.anchor.set(0.5);
    hintText.y = panelH/2 - 40;
    content.addChild(hintText);
    
    this.inspectPopupContainer.addChild(content);
    this.container.addChild(this.inspectPopupContainer);
  }

  private closeInspectPopup(): void {
      if (this.inspectPopupContainer) {
          this.inspectPopupContainer.destroy({ children: true });
          this.inspectPopupContainer = null;
      }
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public setVisible(visible: boolean): void {
    this.container.visible = visible;
  }

  public show(): void {
    this.setVisible(true);
  }

  public hide(): void {
    this.setVisible(false);
  }

  public destroy(): void {
    this.storyManager.removeInventoryEventListener('inventoryChanged', () => {
      this.updateDisplay();
    });
    this.container.destroy({ children: true });
  }
}
